import './App.css'
import { Suspense, lazy } from 'react'
import { Toaster } from "@/components/ui/toaster"
import { Toaster as HotToaster } from 'react-hot-toast'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import VisualEditAgent from '@/lib/VisualEditAgent'
import NavigationTracker from '@/lib/NavigationTracker'
import CookieConsent from '@/components/CookieConsent'
import IdiomaSegunRuta from '@/components/IdiomaSegunRuta'
import GoogleConnectGuideDialog from '@/components/gbusiness/GoogleConnectGuideDialog'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import NotFound404 from './pages/NotFound404';
import ScrollToTop from './lib/ScrollToTop';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { isPublicPath, normalizePath, toRedirectValue } from '@/lib/authRedirect';
import { esRutaDeApp } from '@/lib/rutasApp';
import { CLAVES_PAGINA, IDIOMAS, urlDe } from '@/lib/rutasPublicas';
import LoginRedirect from '@/lib/LoginRedirect';
import Login from './pages/Login';
import Register from './pages/Register';
import PasswordReset from './pages/PasswordReset';
import PasswordResetConfirm from './pages/PasswordResetConfirm';
/*
 * ── SEIS PÁGINAS DEL PANEL QUE YA NO SE IMPORTAN ESTÁTICAS ──────────────────
 *
 * Importadas aquí arriba, arrastraban al bundle que descarga un visitante
 * ANÓNIMO de la landing: `recharts` (557 KB, por `ProductDetail` y
 * `AdminUserDetail`) y `@react-google-maps/api` (324 KB, por
 * `AdminProductDetail` y por `AnalyticsMap`).
 *
 * Tres de ellas —`ProductDetail`, `Profile`, `ProductUpdate`— YA estaban
 * declaradas como `lazy()` en `pages.config.js`, y ese `lazy()` no servía de
 * nada: cuando un módulo tiene import estático Y dinámico, Rollup resuelve al
 * estático y lo sube al chunk padre, así que el chunk separado ni se creaba.
 * Ahora se montan desde `Pages.*` y el `lazy()` funciona.
 *
 * Las otras tres NO están en `PAGES` —se comprobó una por una, y por eso se
 * declaran aquí abajo con su propio `lazy()`. Cambiarlas a `Pages.*` sin mirar
 * habría dejado `element={<undefined />}`: una ruta que no pinta nada y no da
 * ningún error.
 *
 * Todas se ven solo con sesión, así que el `<Suspense>` cae donde ya hay un
 * spinner de carga de datos. Eso las diferencia de `Login`, `Redirect` o las
 * páginas V3, que siguen estáticas porque son entry points públicos y ahí el
 * viaje de ida y vuelta sí se nota.
 */
import LinkTree from './pages/LinkTree';
// Páginas legales públicas (DPA, Aviso Legal, Cookies). Importadas estáticas como
// Login/Register: son entry points públicos y su componente es mínimo (el contenido
// vive en los JSON de locale, ya empaquetados vía i18n.js).
import Dpa from './pages/Dpa';
import AvisoLegal from './pages/AvisoLegal';
import Cookies from './pages/Cookies';
// Redirect (landing de escaneo) se importa ESTÁTICO a propósito (no vía Pages.*
// lazy): es un entry point público crítico y, si fuese lazy, el fallback de
// <Suspense> mostraría un PageSpinner extra mientras baja el chunk → el cliente
// vería dos spinners (el del chunk + el propio del scan). Mismo patrón que LinkTree.
import Redirect from './pages/Redirect';
// Landing (versión "Cálido"): entry-point público en `/`. Importada estática
// (no lazy) — es la primera impresión para no autenticados, evitar el spinner
// de <Suspense>.
import AuditorPage from '@/components/landing/auditor/AuditorPage';
// Las páginas públicas del rediseño. Estáticas y no `lazy` como el resto de
// entry points públicos: un chunk aparte les añadiría un viaje de ida y vuelta
// antes del primer pintado.
import HomeV3 from './pages/v3/HomeV3';
import MultiubicacionesV3 from './pages/v3/MultiubicacionesV3';
import VisibilidadSeoV3 from './pages/v3/VisibilidadSeoV3';
import AutomatizacionesIaV3 from './pages/v3/AutomatizacionesIaV3';
import PublicacionesIaV3 from './pages/v3/PublicacionesIaV3';
import WidgetResenasV3 from './pages/v3/WidgetResenasV3';
import SectorV3 from './pages/v3/SectorV3';
// `/pricing` según la sesión: la página pública o el checkout. Ver su cabecera.
import PreciosSegunSesion from './pages/v3/PreciosSegunSesion';
import SobreNosotrosV3 from './pages/v3/SobreNosotrosV3';
import { CAPTURAS_HABILITADAS } from './lib/capturasGate';
import { SEO_SCORE_V2_HABILITADA } from './lib/seoScoreV2Gate';

/**
 * Las paginas de capturas, cargadas en DIFERIDO y solo si el flag esta puesto.
 *
 * Con un `import` estatico arriba, la pagina y sus datos inventados acababan
 * dentro del bundle de produccion aunque la ruta no se montara — comprobado
 * construyendo con `VITE_APP_ENV=prod` y buscando los nombres en `dist/`. Eran
 * inalcanzables, pero estaban.
 *
 * Con el `import()` detras de una constante que Vite resuelve en tiempo de
 * build, la rama entera se puede eliminar y el chunk no llega a generarse.
 */
const MapaSeoCaptura = CAPTURAS_HABILITADAS
  ? lazy(() => import('./pages/capturas/MapaSeoCaptura'))
  : null;
// La landing del Plano Google Maps pt con datos inventados, para revisarla.
const PlanoGoogleMapsCaptura = CAPTURAS_HABILITADAS
  ? lazy(() => import('./pages/capturas/PlanoGoogleMapsCaptura'))
  : null;
// Los anuncios de ese plan dentro de la app (franjas, Dispositivos y ventana).
const AnunciosPtCaptura = CAPTURAS_HABILITADAS
  ? lazy(() => import('./pages/capturas/AnunciosPtCaptura'))
  : null;
// SEO Score v2 (§175): mismo truco, para que en producción no exista ni el chunk.
// Vive en `pages/seoV2/` y no en `pages/` para que el registro automático de
// `pages.config.js` no la recoja y la monte sin el candado.
const GMBHealthScoreV2 = SEO_SCORE_V2_HABILITADA
  ? lazy(() => import('./pages/seoV2/GMBHealthScoreV2'))
  : null;
// Aceptación de invitación de colaborador: entry point PÚBLICO (sin auth, sin
// Layout) — el invitado aún no tiene cuenta. Importada estática como Login.
import JoinInvitation from './pages/JoinInvitation';
import PaymentLinkStatus from './components/billing/PaymentLinkStatus';
import OfertaPtV4Page from './components/ofertaPt/v4/OfertaPtV4Page';
import { LanguageProvider } from '@/components/LanguageContext';
import PageSpinner from '@/components/PageSpinner';
import ImpersonationBar from '@/components/ImpersonationBar';
import SupportAccessGate from '@/components/support/SupportAccessGate';
import PosthogRecordingGate from '@/lib/PosthogRecordingGate';

const AdminUserDetail = lazy(() => import('./pages/AdminUserDetail'));
const AdminProductDetail = lazy(() => import('./pages/AdminProductDetail'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

/**
 * Qué componente pinta cada página de marketing. Las URLs —las cincuenta, cinco
 * idiomas por página— viven en `lib/rutasPublicas.js`; aquí solo está el
 * emparejamiento con el componente.
 *
 * Las claves tienen que ser exactamente las de `PAGINAS`. Si falta una, su ruta
 * se registraría con `element={undefined}` y no pintaría nada **sin dar ningún
 * error**: una página en blanco con 200, que es lo peor que puede indexarse. De
 * ahí el aviso de debajo.
 */
const COMPONENTE_DE_PAGINA = {
  home: <HomeV3 />,
  multiubicaciones: <MultiubicacionesV3 />,
  visibilidadSeo: <VisibilidadSeoV3 />,
  automatizaciones: <AutomatizacionesIaV3 />,
  publicacionesIa: <PublicacionesIaV3 />,
  widgetResenas: <WidgetResenasV3 />,
  restauracion: <SectorV3 clave="restauracion" />,
  clinicas: <SectorV3 clave="clinicas" />,
  automotriz: <SectorV3 clave="automotriz" />,
  hoteles: <SectorV3 clave="hoteles" />,
  precios: <PreciosSegunSesion />,
  sobreNosotros: <SobreNosotrosV3 />,
};

if (import.meta.env.DEV) {
  const sinComponente = CLAVES_PAGINA.filter((clave) => !COMPONENTE_DE_PAGINA[clave]);
  if (sinComponente.length) {
    console.error('[rutas] páginas declaradas sin componente:', sinComponente.join(', '));
  }
}

/** `/v3/<slug>` → `/<slug>`: los slugs españoles no cambiaron al subir las
 *  páginas a la raíz, así que la redirección es quitar el prefijo. */
function RedirigeDesdeV3() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
}

/**
 * Las `<Route>` de las páginas de marketing, una por página e idioma.
 *
 * `/` se queda fuera y se declara aparte, más abajo: es la única que cambia
 * según haya sesión o no —con sesión es el panel, sin ella la home— y meterla
 * aquí obligaría a arrastrar ese caso hasta esta función.
 */
function rutasDeMarketing() {
  return CLAVES_PAGINA.flatMap((clave) => IDIOMAS.map((idioma) => {
    const url = urlDe(clave, idioma);
    if (url === '/') return null;
    // react-router ignora la barra final al emparejar, pero la tabla la lleva
    // porque para el prerender `/pt/` es un directorio (`dist/pt/index.html`).
    const path = url.length > 1 ? url.replace(/\/+$/, '') : url;
    return <Route key={`${clave}-${idioma}`} path={path} element={COMPONENTE_DE_PAGINA[clave]} />;
  })).filter(Boolean);
}

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, isAuthenticated, navigateToLogin } = useAuth();
  /*
   * `useLocation()` y NO `window.location`: el hook suscribe el componente a
   * los cambios de ruta. Con `window.location` este componente no se enteraba
   * de la navegación de un `<Link>` —la URL cambiaba pero nada lo volvía a
   * pintar— y como el 404 anónimo se devuelve desde aquí, FUERA de `<Routes>`,
   * sus enlaces cambiaban la barra de direcciones y la pantalla se quedaba en
   * el 404.
   */
  const location = useLocation();

  // La lista de rutas públicas vive en @/lib/authRedirect: el interceptor 401
  // de httpClient necesita exactamente la misma, y tenerla en dos sitios es
  // cómo se acaba echando al login a un visitante de una página pública.
  const normalizedPath = normalizePath(location.pathname);
  const esRutaPublica = isPublicPath(location.pathname);

  // El flujo público de escaneo (/redirect, /en1/:id) llama a endpoints AllowAny
  // y gestiona su propio estado de carga, así que NO debe esperar al check de auth
  // (me()): bloquearlo tras el spinner global solo añade un segundo spinner
  // redundante + un round-trip desperdiciado. /login ya gestiona su spinner aparte.
  const skipsAuthSpinner = normalizedPath === '/login' ||
                           normalizedPath === '/redirect' ||
                           normalizedPath.startsWith('/en1/');

  // Show loading spinner while checking app public settings or auth
  if ((isLoadingPublicSettings || (isLoadingAuth && !isAuthenticated)) && !skipsAuthSpinner) {
    return <PageSpinner />;
  }

  // Handle authentication errors and redirects
  if (!isAuthenticated && !esRutaPublica) {
    /*
     * Una URL que NO EXISTE no pide credenciales: enseña el 404.
     *
     * Sin esto, la ruta comodín de más abajo era inalcanzable para un visitante
     * anónimo —este `if` se la comía— y pedir `/pagina-inventada` devolvía el
     * formulario de login. Confuso (te pide sesión para algo que no hay) y
     * además una pista falsa de que la página existiría estando autenticado.
     *
     * `esRutaDeApp` lee la MISMA lista que el `location` de nginx, así que los
     * dos no pueden discrepar: lo que nginx trata como del SPA es lo que aquí
     * lleva al login, y lo que nginx devuelve como 404 es lo que aquí pinta la
     * página de error.
     */
    if (!esRutaDeApp(normalizedPath)) {
      return <NotFound404 />;
    }
    // Se pasa la ruta entera —con query y hash— para volver a ella tras el
    // login: es lo que hace que un enlace de correo a /Reviews?reviewId=123
    // acabe donde tiene que acabar y no en el escritorio por defecto.
    navigateToLogin(toRedirectValue(location));
    return null;
  }

  // Render the main app
  return (
    <Suspense fallback={<PageSpinner />}>
      {/* Fuera de <Routes> a propósito: la barra tiene que verse en TODAS las
          páginas, incluidas las que se renderizan fuera del Layout. */}
      <ImpersonationBar />
      {/* El lado del USUARIO del acceso de soporte con permiso: el modal que
          pide permiso, el banner mientras el soporte está dentro y el aviso
          de salida. Fuera de <Routes> por lo mismo que la barra. */}
      <SupportAccessGate />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/password-reset-confirm/:uid/:token" element={<PasswordResetConfirm />} />
        {/* /redirect (minúscula) es la ruta canónica: TODO apunta ahí — QR
            generados, links del backend y el handler server-side usan
            `/redirect/?id=`. /Redirect (mayúscula) es un alias defensivo.
            AMBOS deben renderizar el MISMO componente; la lógica de auth/público
            de arriba ya normaliza el casing vía `normalizedPath`. */}
        <Route path="/Redirect" element={<Redirect />} />
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/en1/:id" element={<LinkTree />} />
        {/* Aceptación de invitación de colaborador — público, sin Layout. */}
        <Route path="/join" element={<JoinInvitation />} />
        {/* Enlace de pago de ventas que no pudo ir a Stripe (caducado, ya es
            cliente…). Público, sin Layout: llega desde un WhatsApp. */}
        <Route path="/link-pago" element={<PaymentLinkStatus />} />
        {/* La landing del Plano Google Maps pt, sin Layout. Dos puertas: con
            el enlace del correo (pública, el token abre sesión al pulsar
            «Começar o meu plano») y con sesión (la de arriba manda al login). */}
        <Route path="/oferta/:token" element={<OfertaPtV4Page />} />
        <Route path="/plano-google-maps" element={<OfertaPtV4Page />} />
        {/* Páginas legales públicas. Los alias capitalizados (`/Terms`,
            `/Privacy`, `/Dpa`, `/LegalNotice`, `/Cookies`) YA NO están aquí: no
            hacían nada —react-router casa sin distinguir mayúsculas, ver
            `authRedirect.js`— y lo que sí hacían era servir el mismo contenido
            en dos URLs con 200 las dos. Ahora la capitalizada lleva un 301 a la
            minúscula desde `nginx-frontend.conf`, que es donde una redirección
            puede ser permanente de verdad. */}
        <Route path="/terms" element={<Pages.Terms />} />
        <Route path="/privacy" element={<Pages.Privacy />} />
        <Route path="/dpa" element={<Dpa />} />
        <Route path="/legal-notice" element={<AvisoLegal />} />
        <Route path="/cookies" element={<Cookies />} />
        {/* `/pricing`, `/funciones`, `/casos-de-uso` y `/dispositivos` eran de
            la V2 y ya no existen. NO se declaran aquí como `<Navigate>`: sus
            redirecciones van en `nginx-frontend.conf` y son 301 de verdad.
            Un `<Navigate>` de React devuelve 200 con la cáscara del SPA y
            redirige después con JavaScript, así que para Google no es una
            redirección permanente y las posiciones de esas tres urls no se
            traspasarían. Con un 301 sí. */}
        {/* Las páginas de marketing en sus cinco idiomas, generadas desde
            `lib/rutasPublicas.js`. Enumerarlas a mano serían cincuenta líneas
            que además habría que mantener a la par del sitemap y de los
            hreflang — y el día que se separen, el sitemap anuncia una URL que
            el router no sirve y el fallo se ve en Search Console semanas
            después.

            La home española (`/`) NO está: la sigue sirviendo la V2 hasta el PR
            que cambie la raíz. Las demás llevan `noindex` desde
            `LandingShellV3` mientras tanto, así que se pueden revisar en
            staging sin que Google vea nada. */}
        {rutasDeMarketing()}

        {/* Las viejas `/v3/*` ya no existen, pero están pegadas en revisiones y
            en el chat del equipo. Redirigen en vez de dar un 404: los slugs
            españoles son los mismos (`/v3/hoteles` → `/hoteles`), así que basta
            con quitar el prefijo. `replace` para no dejarlas en el historial.

            No llevan valor de SEO que preservar —nacieron con `noindex` y nunca
            se enlazaron desde ninguna parte—, así que esto es cortesía con el
            equipo y no una redirección que haya que mantener: se puede borrar
            en cuanto nadie la eche de menos. */}
        <Route path="/v3" element={<Navigate to="/" replace />} />
        <Route path="/v3/:slug" element={<RedirigeDesdeV3 />} />

        {/* Paginas de CAPTURAS: el producto con datos inventados, para poder
            fotografiarlo para la landing. Se sirven en desarrollo y en
            STAGING, nunca en produccion — el mapa es Google Maps de verdad y
            solo con la clave de un entorno desplegado se ve como se va a ver.
            Ver `lib/capturasGate.js`.

            El guard va aqui y no solo dentro del componente porque lo que
            importa es que la ruta no exista: una pantalla con datos inventados
            con pinta de reales, accesible en produccion, acaba indexada. El
            componente ademas se niega a pintar por su cuenta. */}
        {MapaSeoCaptura ? (
          <Route path="/capturas/mapa-seo" element={<MapaSeoCaptura />} />
        ) : null}
        {PlanoGoogleMapsCaptura ? (
          <Route path="/capturas/plano-google-maps" element={<PlanoGoogleMapsCaptura />} />
        ) : null}
        {AnunciosPtCaptura ? (
          <Route path="/capturas/anuncios-pt" element={<AnunciosPtCaptura />} />
        ) : null}
        {/* Auditor SEO — pública y SIN enlazar desde ningún sitio. Está para
            poder probarlo en staging contra Google de verdad sin meterlo en la
            landing. En producción es inofensiva: con `PUBLIC_AUDIT_ENABLED`
            apagado el componente no pinta nada y la página queda en blanco. */}
        <Route path="/auditor" element={<AuditorPage />} />
        {/* La raíz. Con sesión es el panel; sin ella, la home V3 — que desde
            este PR es LA home y no una preview en `/v3`.

            Es la única página de marketing que no sale de `rutasDeMarketing()`,
            justamente por ese doble papel. La versión V2 (`<Landing />`) deja de
            servirse aquí; el componente y sus hermanos `/funciones`,
            `/casos-de-uso` y `/dispositivos` siguen en el repo hasta el PR que
            los borre con sus 301, porque esas tres SÍ tienen historial en
            Search Console y desaparecer sin redirección tira posiciones. */}
        <Route path="/" element={
          isAuthenticated ? (
            <LayoutWrapper currentPageName={mainPageKey}>
              <MainPage />
            </LayoutWrapper>
          ) : <HomeV3 />
        } />
        <Route path="/ProductDetail/:id" element={
          isAuthenticated ? (
            <LayoutWrapper currentPageName="ProductDetail">
              <Pages.ProductDetail />
            </LayoutWrapper>
          ) : <LoginRedirect />
        } />
        <Route path="/Profile" element={
          isAuthenticated ? (
            <LayoutWrapper currentPageName="Profile">
              <Pages.Profile />
            </LayoutWrapper>
          ) : <LoginRedirect />
        } />
        <Route path="/ProductUpdate/:id" element={
          isAuthenticated ? (
            <LayoutWrapper currentPageName="ProductUpdate">
              <Pages.ProductUpdate />
            </LayoutWrapper>
          ) : <LoginRedirect />
        } />
        <Route path="/AdminUserDetail/:id" element={
          isAuthenticated ? (
            <LayoutWrapper currentPageName="AdminUsers">
              <AdminUserDetail />
            </LayoutWrapper>
          ) : <LoginRedirect />
        } />
        <Route path="/AdminProductDetail/:id" element={
          isAuthenticated ? (
            <LayoutWrapper currentPageName="AdminProducts">
              <AdminProductDetail />
            </LayoutWrapper>
          ) : <LoginRedirect />
        } />
        <Route path="/ChangePassword" element={
          isAuthenticated ? (
            <LayoutWrapper currentPageName="ChangePassword">
              <ChangePassword />
            </LayoutWrapper>
          ) : <LoginRedirect />
        } />
        {/* RegisterQR: configuración de un dispositivo nuevo. Se renderiza SIN
              Layout (sin sidebar/nav) a propósito, para que el usuario no salga
              por error sin terminar de configurar el dispositivo. La página ya es
              full-screen con su propia cabecera. */}
          <Route path="/RegisterQR" element={
            isAuthenticated ? <Pages.RegisterQR /> : <LoginRedirect />
          } />
          <Route path="/location/:id/:slug" element={
            isAuthenticated ? (
              <LayoutWrapper currentPageName="LocationDetail">
                <Pages.LocationDetail />
              </LayoutWrapper>
            ) : <LoginRedirect />
          } />
        {/* Settings con tab en la URL (/settings/{places|team|integrations|billing|other}) —
            un refresh conserva la pestaña activa. La ruta sin tab (/settings) la
            sigue montando el loop de abajo (default: gestión local). */}
        <Route
          path="/settings/:tab"
          element={
            isAuthenticated ? (
              <LayoutWrapper currentPageName="settings">
                <Pages.settings />
              </LayoutWrapper>
            ) : <LoginRedirect />
          }
        />
        {/* Integraciones pasó a ser una pestaña de Configuración. La ruta vieja
            se mantiene como redirección: estaba en el menú, así que hay
            marcadores y enlaces por ahí que no deben acabar en un 404. */}
        <Route path="/Integraciones" element={<Navigate to="/settings/integrations" replace />} />
        {GMBHealthScoreV2 ? (
          <Route path="/GMBHealthScoreV2" element={
            isAuthenticated ? (
              <LayoutWrapper currentPageName="GMBHealthScore">
                <GMBHealthScoreV2 />
              </LayoutWrapper>
            ) : <LoginRedirect />
          } />
        ) : null}
        {Object.entries(Pages).map(([path, Page]) => {
          // Skip auth pages and dynamic pages from the auto-generated layout routes
          if (['Login', 'Register', 'PasswordReset', 'ProductDetail', 'Terms', 'Privacy', 'Pricing', 'Redirect', 'RegisterQR', 'Integraciones'].includes(path)) return null;
          
          return (
            <Route
              key={path}
              path={`/${path}`}
              element={
                isAuthenticated ? (
                  <LayoutWrapper currentPageName={path}>
                    <Page />
                  </LayoutWrapper>
                ) : <LoginRedirect />
              }
            />
          );
        })}
        <Route path="*" element={<NotFound404 />} />
      </Routes>
    </Suspense>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <LanguageProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <NavigationTracker />
            {/* En las páginas públicas el idioma lo decide la URL, y hay que
                reaplicarlo en cada navegación porque el detector de i18next
                solo corre al arrancar. Aquí dentro por lo mismo que el gate de
                Posthog de abajo: usa useLocation(). */}
            <IdiomaSegunRuta />
            {/* Decide si la sesión se graba (session replay). Aquí y no dentro
                de <AuthenticatedApp /> porque usa useLocation() y ese
                componente lee window.location a propósito, sin suscribirse a
                los cambios de ruta. */}
            <PosthogRecordingGate />
            <CookieConsent />
            {/* El paso previo a la pantalla de permisos de Google. Uno solo
                para toda la app: lo abre cualquier botón de «conectar» vía
                `abrirGuiaGoogle()`. Ver `lib/googleConnectGuide.js`. */}
            <GoogleConnectGuideDialog />
            <AuthenticatedApp />
          </Router>
        </LanguageProvider>
        <Toaster />
        {/* Duración por tipo, no global: un `duration` en la raíz de
            toastOptions pisa también a los errores, y con 1 s no daba tiempo
            a leerlos. El éxito se ve de reojo; el error hay que leerlo y
            entender qué hacer. `loading` no caduca. Los toasts que pasan su
            propio `duration` (throttle en httpClient, PaymentLinkModal) lo
            siguen respetando. */}
        <HotToaster
          position="top-right"
          toastOptions={{
            success: { duration: 2500 },
            error: { duration: 6000 },
          }}
        />
        <VisualEditAgent />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App
